import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ProductStockData, UpdateStockData } from '@/services/branchs';

interface UpdatePriceTabProps {
  stockData?: ProductStockData | null;
  newPrice: string;
  onPriceChange: (value: string) => void;
  onSave: (payload: UpdateStockData) => void;
  isSaving?: boolean;
}

export function UpdatePriceTab({
  stockData,
  newPrice,
  onPriceChange,
  onSave,
  isSaving = false,
}: UpdatePriceTabProps) {
  const handleSave = () => {
    onSave({
      quantity: stockData?.quantity || 0,
      price: newPrice,
      sellPrice: stockData?.sellPrice || newPrice,
      isActive: stockData?.isActive ?? true,
      branchId: stockData?.branch?.id || '',
      productId: stockData?.product?.id || '',
    });
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Update Product Price</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-muted-foreground">Current Price</Label>
            <div className="text-2xl font-semibold mt-1">
              ${stockData?.price || '0.00'}
            </div>
          </div>
          <div>
            <Label htmlFor="newPrice">New Price</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-2.5 text-muted-foreground">
                $
              </span>
              <Input
                id="newPrice"
                type="number"
                min="0"
                step="0.01"
                value={newPrice}
                onChange={(e) => onPriceChange(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </div>

        <div className="pt-4">
          <Button
            onClick={handleSave}
            className="w-full"
            disabled={isSaving || !newPrice || parseFloat(newPrice) <= 0}
          >
            {isSaving ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </>
            ) : (
              'Update Price'
            )}
          </Button>
        </div>

        {newPrice &&
          parseFloat(newPrice) !== parseFloat(stockData?.price || '0') && (
            <div className="mt-4 p-3 bg-muted/50 rounded-md">
              <p className="text-sm">
                <span className="font-medium">Price change:</span> $
                {stockData?.price || '0.00'} → ${newPrice}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                This will update the price for all units of this product.
              </p>
            </div>
          )}
      </CardContent>
    </Card>
  );
}
